import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupplierQuery } from 'api';
import { TablePaginationConfig, TableProps } from 'components';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { OrderType, PaginationType, SortParamsType } from 'typings';
import { SupplierDataType } from 'pages/admin/suppliers/index';

export interface IFilterParams {
  name?: string;
  tags?: string[];
  countryId?: string;
  industryId?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  minPurchaseRequests?: number;
  maxPurchaseRequests?: number;
}

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};

export const useSuppliers = () => {
  const [addModalState, setAddModalState] = useState<boolean>(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState<IFilterParams>({});
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [sort, setSort] = useState<SortParamsType>(initialSort);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  const { data: { content = [], totalElements = 0 } = {}, isLoading } = useSupplierQuery({
    size: pagination.size,
    page: pagination.page,
    sortField: sort.field,
    sortOrder: sort.order,
    search,
    filters,
  });
  const onOpenFilters = useCallback(() => setOpenFilters(true), []);
  const onCloseFilters = useCallback(() => setOpenFilters(false), []);

  const onAddModalStateChange = useCallback(() => {
    setAddModalState((prevState) => !prevState);
  }, []);
  const routeChange = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.suppliers.view}/${value}/general`);
      setAddModalState(false);
    },
    [navigate]
  );

  const handleTableChange: TableProps<SupplierDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<SupplierDataType> | SorterResult<SupplierDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  const onSearch = useCallback((value: string) => {
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setSearch(value);
  }, []);

  const applyFilters = useCallback(
    (values: Record<string, any>) => {
      const _filters: any = {
        ...(values.supplierId && { supplierId: values.supplierId }),
        ...(values.countryId && { countryId: values.countryId }),
        ...(values.languageId && { languageId: values.languageId }),
        ...(values.industryId && { industryId: values.industryId }),
        ...(values.type && { type: values.type }),
        ...(values.tags?.length && { tags: values.tags }),
        ...(values.createdDate?.[0] && {
          createdDateFrom: dateFormatter(values.createdDate[0], 'start'),
        }),
        ...(values.createdDate?.[1] && {
          createdDateTo: dateFormatter(values.createdDate[1], 'end'),
        }),
        ...(values.lastUpdatedDate?.[0] && {
          lastUpdatedDateFrom: dateFormatter(values.lastUpdatedDate[0], 'start'),
        }),
        ...(values.lastUpdatedDate?.[1] && {
          lastUpdatedDateTo: dateFormatter(values.lastUpdatedDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );
  const resetFilters = useCallback(() => setFilters({}), []);

  return {
    onOpenFilters,
    onCloseFilters,
    onAddModalStateChange,
    addModalState,
    routeChange,
    data: content,
    total: totalElements ?? 0,
    handleTableChange,
    openFilters,
    applyFilters,
    resetFilters,
    pagination,
    isLoading,
    onSearch,
  };
};
